import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { TableCell, TableRow } from '@mui/material';
import { Label } from '@components/Label';
import { Dropdown } from '@components/dropdown';
import { TableBodyCell } from '@components/table';
import { fonts } from '@theme/fontsCustomer';
import type { BillingStatus, Filter } from '@repositories/monthlyBillingRepository/monthlyBilling.types';
import { useUpdateMonthlyBillingStatus } from '@queryHooks/useMonthlyBilling';
import { dateFormat } from '@utils/dateUtil';
import { currencyNumberFormat } from '@utils/numberFormat';
import type { SubscriptionBillingModel } from '@repositories/monthlyBillingRepository/models/SubscriptionBillingModel';

interface Props {
  billing: SubscriptionBillingModel;
  queries: Filter;
}

export const BillingRow = ({ billing, queries }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const param = useParams();
  const { mutate: updateMonthlyBillingStatus } = useUpdateMonthlyBillingStatus();

  const handleClick = (id: string) => {
    navigate(`/manager/${param.tenantId}/billings/history/subscription/${id}`);
  };

  const handleChangeBillingStatus = (value: string | number) => {
    updateMonthlyBillingStatus({ id: billing.id, status: value as BillingStatus });
  };

  const renderBillingStatusDropdown = () => {
    return billing.isExternalSubscription ? (
      <StateDropdown value={billing.status} onChange={handleChangeBillingStatus} variant="no-line" spaceBetweenMenu={3}>
        <Dropdown.Menu value="CREATED">
          <Label variant="solid" color="yellow">
            {t('Acc_Detail_07')}
          </Label>
        </Dropdown.Menu>
        <Dropdown.Menu value="PAID">
          {' '}
          <Label variant="solid" color="gray">
            {t('Billing.status.PAID')}
          </Label>
        </Dropdown.Menu>
      </StateDropdown>
    ) : (
      <Label variant="solid" color={billing.isForecasted ? 'blue' : billing.isPayment ? 'gray' : 'yellow'}>
        {billing.statusLabel}
      </Label>
    );
  };

  return (
    <TableRow>
      <TableBodyCell $isFilterApplied={!!queries.monthFrom || !!queries.monthTo}>
        {dateFormat(billing.month, '-', t('DateFormat_YM'))}
      </TableBodyCell>
      <TableCell>
        {`${dateFormat(billing.startDate, '-', t('DateFormat_YMD'))} ~ ${dateFormat(
          billing.endDate,
          '-',
          t('DateFormat_YMD'),
        )}`}
      </TableCell>
      <TableCell>{currencyNumberFormat(billing.originalCost, billing.originalCurrencyUnit, 'text')}</TableCell>
      <TableCell>{currencyNumberFormat(billing.cost, billing.currencyUnit, 'text')}</TableCell>
      <TableCell>{dateFormat(billing.paymentDate, '-', t('DateFormat_YMD'))}</TableCell>
      <TableCell>{renderBillingStatusDropdown()}</TableCell>
      <StyledCell onClick={() => handleClick(billing.id)}>{t('Subscrib_Detail_Acc')}</StyledCell>
    </TableRow>
  );
};

const StyledCell = styled(TableCell)`
  color: ${({ theme }) => theme.colors['text-blue-light']};
  &:hover {
    text-decoration: underline;
  }
  cursor: pointer;
`;

const StateDropdown = styled(Dropdown)`
  ${fonts.Headline8}
  width: 110px;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;
